'use client'

import { useCallback, useRef, useState, type KeyboardEvent, type UIEvent } from 'react'

import { MediaImage } from '@/components/MediaImage'
import type { Media } from '@/payload-types'

import stili from './ArticleGallery.module.css'

type Props = {
  images: Media[]
}

export function ArticleGallery({ images }: Props) {
  const viewport = useRef<HTMLDivElement>(null)
  const [current, setCurrent] = useState(0)
  const multiple = images.length > 1

  const goTo = useCallback(
    (next: number) => {
      const element = viewport.current
      if (!element || next < 0 || next >= images.length) return

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      element.scrollTo({ left: next * element.clientWidth, behavior: reducedMotion ? 'auto' : 'smooth' })
      setCurrent(next)
    },
    [images.length],
  )

  const onScroll = useCallback((event: UIEvent<HTMLDivElement>) => {
    const { clientWidth, scrollLeft } = event.currentTarget
    if (!clientWidth) return
    setCurrent(Math.max(0, Math.min(images.length - 1, Math.round(scrollLeft / clientWidth))))
  }, [images.length])

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        goTo(current - 1)
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault()
        goTo(current + 1)
      }
    },
    [current, goTo],
  )

  if (images.length === 0) return null

  return (
    <section
      aria-label="Galleria fotografica dell’articolo"
      className={stili.gallery}
      onKeyDown={multiple ? onKeyDown : undefined}
      tabIndex={multiple ? 0 : undefined}
    >
      <div className={stili.viewport} onScroll={onScroll} ref={viewport}>
        {images.map((image, index) => {
          const caption = image.caption?.trim()

          return (
            <figure
              aria-label={multiple ? `Immagine ${index + 1} di ${images.length}` : undefined}
              className={stili.slide}
              key={image.id}
            >
              <div className={stili.imageFrame}>
                <MediaImage media={image} formato="article" sizes="(max-width: 1000px) 100vw, 1000px" />
              </div>
              {caption && <figcaption className={stili.caption}>{caption}</figcaption>}
            </figure>
          )
        })}
      </div>

      {multiple && (
        <div className={stili.controls}>
          <button
            aria-label="Immagine precedente"
            className={stili.button}
            disabled={current === 0}
            onClick={() => goTo(current - 1)}
            type="button"
          >
            <span aria-hidden="true">←</span>
          </button>
          <p aria-live="polite" className={stili.position}>
            {current + 1} / {images.length}
          </p>
          <button
            aria-label="Immagine successiva"
            className={stili.button}
            disabled={current === images.length - 1}
            onClick={() => goTo(current + 1)}
            type="button"
          >
            <span aria-hidden="true">→</span>
          </button>
        </div>
      )}
    </section>
  )
}
